import { useEffect, useRef, useState, useCallback } from 'react'
import JsSIP from 'jssip'
import { apiGet, apiPost } from '../../api/client.js'
import { getOrCreateSipCredential } from './sipCredentialCache.js'
import { buildIceServers } from './iceServers.js'
import { attachIceFailureGuard } from './ice-failure-guard.js'
import { timestampMs } from '../../utils/timezone.js'
import { shouldAutoAnswer } from './auto-answer.js'
import { createSessionRegistry } from './session-registry.js'
import { createAttendedTransfer } from './attended-transfer.js'
import { useCallStore } from '../../store/callStore.js'

/**
 * How long to let ICE gathering run before sending the INVITE anyway.
 *
 * Chrome will not report gathering complete until every configured ICE server
 * has replied or timed out (~10s for TURN), and JsSIP holds the INVITE until
 * then — so a TURN server that is merely slow delays the call itself, not just
 * the media. Host and server-reflexive candidates are present within a few
 * hundred milliseconds; this budget keeps a comfortable margin over that while
 * staying far below the point a user would notice.
 *
 * See the detailed note at the `icecandidate` handler in `onNewRTCSession`.
 */
const ICE_GATHERING_CAP_MS = 1500

/**
 * ITEM 98 — the target-URI rule, in one place.
 *
 * `transfer()` and `dial()` each carried their own copy of this three-branch
 * logic. The attended-transfer path needs it too, and a third copy would be a
 * third thing to drift.
 */
export function buildTargetUri(target: string, tenantDomain?: string): string {
  if (target.includes('@')) return `sip:${target}`
  if (/^\d+$/.test(target)) {
    const tenantSlug = tenantDomain?.split('.')[0] ?? 'localhost'
    return `sip:${tenantSlug}_${target}@${tenantDomain ?? ''}`
  }
  return `sip:${target}@${tenantDomain ?? ''}`
}

/**
 * Drop the SHARED call state when this softphone's call is over.
 *
 * THE DEFECT THIS FIXES
 * ---------------------
 * `useSoftphone` owns the SIP session but never touched `useCallStore`, and
 * `clearCall()` had NO callers anywhere in the app. The only writer of
 * `activeCall` was the server socket feed, whose `setEnded` marks the call
 * `status: 'ended'` and deliberately leaves the object in place — it never
 * nulls it.
 *
 * So `activeCall` was set on answer and then stayed non-null forever. Anything
 * keyed on its mere presence — AgentWorkspaceLegacy renders its entire call
 * panel on `{activeCall ? …}`, AgentLayout mounts the screen pop — kept showing
 * a call that had already hung up. To the agent this looks like "the phone hung
 * up but the softphone did not", and the next call inherits the stale panel.
 *
 * Clearing it here is correct because the softphone is the authority on its own
 * session: JsSIP's `ended`/`failed` fire on a BYE from either side, on a failed
 * INVITE, and on transport loss. The caller MUST skip this when a consultation
 * leg was promoted — the agent is still talking, just on the other leg, and
 * clearing then would hide a live call.
 */
function releaseSharedCallState(): void {
  useCallStore.getState().clearCall()
}

/**
 * Check whether a JsSIP RTCSession is terminated (status 8).
 *
 * JsSIP RTCSession status constants:
 *   0 NULL, 1 INVITE_SENT, 2 1XX_RECEIVED, 3 INVITE_RECEIVED,
 *   4 WAITING_FOR_ANSWER, 5 ANSWERED, 6 WAITING_FOR_ACK,
 *   7 CANCELED, 8 TERMINATED
 *
 * `answer()` only succeeds in status 4; calling it on any other status throws
 * INVALID_STATE_ERROR. This helper lets us detect dead sessions that are still
 * referenced by `sessionRef` or the session registry so we can clean them up
 * before they cause a crash.
 */
function isSessionTerminated(session: unknown): boolean {
  const status = (session as { status?: number }).status
  return status !== undefined && status === 8
}

export interface SoftphoneConfig {
  agentId: string
  tenantDomain: string
  wsUrl: string
  /**
   * ITEM 99 — the agent's `autoAnswerIncomingCalls` profile flag.
   *
   * Settable in the admin UI (TelephonyTab.tsx:555) and persisted by the API
   * (routes/agents/update.ts:100), and read by NOTHING until now — an admin
   * could turn it on, see it saved, and have nothing change.
   */
  autoAnswerIncomingCalls?: boolean
}

interface SipCreds {
  password: string
  sipUsername: string
  sipDomain: string
  sipUri?: string
}

export type CallState = 'idle' | 'ringing_in' | 'ringing_out' | 'in_call' | 'on_hold'

export interface AudioDevice {
  deviceId: string
  label: string
  kind: 'audioinput' | 'audiooutput'
}

// JsSIP's @types/jssip is unmaintained; narrow surfaces we touch.
interface JsSipSession {
  on: (event: string, cb: (...args: unknown[]) => void) => void
  answer: (opts?: { mediaConstraints?: { audio: boolean; video: boolean }; pcConfig?: RTCConfiguration }) => void
  terminate: () => void
  hold?: () => void
  unhold?: (options?: Record<string, never>, done?: () => void) => boolean | void
  isOnHold?: () => { local: boolean; remote: boolean }
  mute?: (opts: { audio: boolean }) => void
  unmute?: (opts: { audio: boolean }) => void
  sendDTMF?: (tone: string, options: { transportType: 'RFC2833' | 'INFO'; duration: number; interToneGap: number }) => void
  connection?: RTCPeerConnection
  remote_identity?: { uri?: { user?: string }; display_name?: string }
}

interface JsSipUaInstance {
  start: () => void
  stop: () => void
  on: (event: string, cb: (...args: unknown[]) => void) => void
  call: (
    target: string,
    opts?: { mediaConstraints?: { audio: boolean; video: boolean }; pcConfig?: RTCConfiguration },
  ) => JsSipSession
  isRegistered: () => boolean
}

interface NewRtcSessionEvent {
  session: JsSipSession
  originator: string
}

export interface UaConfig {
  sockets: unknown[]
  uri: string
  password: string
  register: boolean
  session_timers: boolean
  /**
   * ITEM 5: previously absent entirely, so JsSIP built the RTCPeerConnection
   * with an empty ICE server list — host candidates only, no STUN reflexive,
   * no TURN relay. Any agent behind NAT got no audio.
   */
  pcConfig: RTCConfiguration
}

export function buildUaConfig(
  config: SoftphoneConfig,
  password: string,
  sipUsername?: string,
  iceOptions?: { useStun?: boolean },
): UaConfig {
  // If sipUsername is provided (from SIP credentials endpoint), use it
  // Otherwise fall back to agentId (for backward compatibility)
  const username = sipUsername || config.agentId

  const { iceServers, warnings } = buildIceServers({ useStun: iceOptions?.useStun ?? true })
  for (const w of warnings) {
    // Loud rather than silent: an unconfigured relay must not look like a
    // working one. See infra/compose/docker-compose.yml `coturn`.
    console.error(`[softphone][ICE] ${w}`)
  }

  return {
    sockets: [new JsSIP.WebSocketInterface(config.wsUrl)],
    uri: `sip:${username}@${config.tenantDomain}`,
    password,
    register: true,
    session_timers: false,
    pcConfig: { iceServers },
  }
}

export function useSoftphone(config: SoftphoneConfig | null) {
  const uaRef = useRef<JsSipUaInstance | null>(null)
  // Each RTCSession builds its own RTCPeerConnection — passing pcConfig at UA
  // construction time (buildUaConfig) only affects JsSIP's own internal
  // default, not the session.answer()/ua.call() calls below, which need it
  // passed explicitly per-call or fall back to an ICE-server-less
  // RTCPeerConnection (host candidates only, no STUN/TURN — see ITEM 5
  // above). Stashed here so answer() has access to the same config the UA
  // was built with.
  const pcConfigRef = useRef<RTCConfiguration>({ iceServers: [] })
  const sessionRef = useRef<JsSipSession | null>(null)
  // ITEM 98 — the second leg an attended transfer needs.
  //
  // `sessionRef` holds the PRIMARY call and is read at 17 sites, all of which
  // assume a single session. Rather than rewrite every one of them on a live
  // media path, the registry tracks BOTH legs so nothing is orphaned, and
  // sessionRef continues to mean "the primary call" for existing callers.
  const sessionsRef = useRef(createSessionRegistry())
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null)
  const ringbackAudioRef = useRef<HTMLAudioElement | null>(null)
  const ringtoneAudioRef = useRef<HTMLAudioElement | null>(null)
  const busyToneAudioRef = useRef<HTMLAudioElement | null>(null)
  // AudioContexts are created in 'suspended' state by the browser until a user
  // gesture unlocks them. We store them here so we can call resume() at play
  // time (after the agent has interacted with the page — echo test, "Make
  // Available", etc.). Without this, the oscillators run but produce silence
  // because the context is never resumed. Applies to ringback and the
  // end-of-call beep — the incoming ringtone is an audio file now and has no
  // context of its own.
  const ringbackCtxRef = useRef<AudioContext | null>(null)
  const busyToneCtxRef = useRef<AudioContext | null>(null)
  // Timestamp of the last end-of-call beep — JsSIP fires BOTH 'ended' and
  // 'failed' for one termination (and each leg of a consult has handlers of
  // its own), which stacked 3s busy tones on top of each other and sounded
  // like several beeps. The guard collapses every end signal inside a call's
  // teardown window into ONE beep.
  const lastEndBeepAtRef = useRef(0)
  const [registered, setRegistered] = useState(false)
  const [callState, setCallState] = useState<CallState>('idle')
  const [remoteCLI, setRemoteCLI] = useState<string | null>(null)
  const [isMuted, setIsMuted] = useState(false)
  // ITEM 100 — expose the active RTCPeerConnection so useWebRtcStats can poll
  // it. The connection was already read internally (session.connection below)
  // and never surfaced, so the quality meter in AgentHeaderBar had nothing to
  // display and sat permanently empty.
  //
  // STATE, not a ref: useWebRtcStats keys its effect on this value, and a ref
  // would not trigger it when a call starts — the meter would stay empty for
  // the whole call and only wake on some unrelated re-render.
  const [peerConnection, setPeerConnection] = useState<RTCPeerConnection | null>(null)
  const [callStartTime, setCallStartTime] = useState<number | null>(null)
  // SIP password lives only in component state — never in localStorage
  const [sipCreds, setSipCreds] = useState<SipCreds | null>(null)

  // Audio device management
  const [audioInputDevices, setAudioInputDevices] = useState<AudioDevice[]>([])
  const [audioOutputDevices, setAudioOutputDevices] = useState<AudioDevice[]>([])
  const [selectedAudioInput, setSelectedAudioInput] = useState<string>('')
  const [selectedAudioOutput, setSelectedAudioOutput] = useState<string>('')
  const [echoTestPassed, setEchoTestPassed] = useState(false)
  const [echoTestRunning, setEchoTestRunning] = useState(false)
  const isEchoTestCallRef = useRef(false)
  const echoTestStreamRef = useRef<MediaStream | null>(null)
  // Track call details for logging to /calls/log
  const callLogRef = useRef<{
    uniqueId: string
    fromNumber: string
    toNumber: string
    direction: 'INBOUND' | 'OUTBOUND' | 'INTERNAL'
    startTime: string
    answerTime?: string
  } | null>(null)
  // Keep agentId in a ref so logCall (memoized with []) always reads the latest value
  const agentIdRef = useRef<string | undefined>(config?.agentId)
  agentIdRef.current = config?.agentId

  // Web Telephony Options (persisted in localStorage)
  const [useStereo, setUseStereo] = useState(() => {
    try { return localStorage.getItem('kc3_useStereo') === 'true' } catch { return false }
  })
  const [useStun, setUseStun] = useState(() => {
    try { return localStorage.getItem('kc3_useStun') !== 'false' } catch { return true }
  })
  // Read inside the UA-construction effect without adding useStun to its deps
  // (which would tear down and rebuild the UA on every toggle). Same pattern as
  // agentIdRef above. Takes effect on the next registration.
  const useStunRef = useRef(useStun)
  useStunRef.current = useStun
  const [enableAgc, setEnableAgc] = useState(() => {
    try { return localStorage.getItem('kc3_enableAgc') === 'true' } catch { return true }
  })
  const [enableEchoCancel, setEnableEchoCancel] = useState(() => {
    try { return localStorage.getItem('kc3_enableEchoCancel') === 'true' } catch { return true }
  })
  const [enableNoiseSupp, setEnableNoiseSupp] = useState(() => {
    try { return localStorage.getItem('kc3_enableNoiseSupp') === 'true' } catch { return true }
  })
  const [enableHighpass, setEnableHighpass] = useState(() => {
    try { return localStorage.getItem('kc3_enableHighpass') === 'true' } catch { return false }
  })

  // Create audio element for remote stream
  useEffect(() => {
    const audio = document.createElement('audio')
    audio.autoplay = true
    remoteAudioRef.current = audio
    return () => {
      audio.srcObject = null
      audio.remove()
    }
  }, [])

  // TSV-03 speaker device change: apply selected audio output to remote audio.
  // setSinkId was never called - speaker selection was dead code.
  useEffect(() => {
    const audio = remoteAudioRef.current
    if (!audio || !selectedAudioOutput) return
    const sinkSupported = typeof (audio as unknown as { setSinkId?: unknown }).setSinkId === 'function'
    if (!sinkSupported) return
    ;(audio as unknown as { setSinkId: (id: string) => Promise<void> })
      .setSinkId(selectedAudioOutput)
      .catch((err: unknown) => console.warn('[useSoftphone] setSinkId failed:', err))
  }, [selectedAudioOutput])

  // TSV-03 microphone device change: acquire MediaStream with selected input
  // device for session.answer and ua.call. Without this, JsSIP internal
  // getUserMedia always used the default mic, ignoring agent selection.
  const getCallMediaStream = useCallback(async (): Promise<MediaStream | undefined> => {
    if (!selectedAudioInput) return undefined
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: selectedAudioInput } },
        video: false,
      })
    } catch (err) {
      console.warn('[useSoftphone] selected input unavailable, using default:', err)
      return undefined
    }
  }, [selectedAudioInput])

  // Create ringback tone (when calling out)
  useEffect(() => {
    const audio = document.createElement('audio')
    audio.loop = true
    // Generate ringback tone using Web Audio API
    const audioContext = new AudioContext()
    ringbackCtxRef.current = audioContext
    const oscillator1 = audioContext.createOscillator()
    const oscillator2 = audioContext.createOscillator()
    const gainNode = audioContext.createGain()

    oscillator1.frequency.value = 440 // A4
    oscillator2.frequency.value = 480 // ~B4
    // Starts silent — the cadence pulses it while the ringback is playing.
    // A constant gain meant the agent heard one unbroken tone for the whole
    // outbound ring instead of "brrring... brrring...".
    gainNode.gain.value = 0

    const destination = audioContext.createMediaStreamDestination()
    oscillator1.connect(gainNode)
    oscillator2.connect(gainNode)
    gainNode.connect(destination)

    // Start oscillators immediately
    oscillator1.start()
    oscillator2.start()

    // Same NANP cadence as the incoming ringtone: 2s on / 4s off.
    const RINGBACK_ON_SEC = 2
    const RINGBACK_OFF_SEC = 4
    let ringbackCadenceTimer: ReturnType<typeof setInterval> | null = null
    const startRingbackCadence = (): void => {
      if (ringbackCadenceTimer !== null) return
      const pulse = (): void => {
        const t = audioContext.currentTime
        gainNode.gain.setTargetAtTime(0.1, t, 0.01)
        gainNode.gain.setTargetAtTime(0, t + RINGBACK_ON_SEC, 0.01)
      }
      pulse()
      ringbackCadenceTimer = setInterval(pulse, (RINGBACK_ON_SEC + RINGBACK_OFF_SEC) * 1000)
    }
    const stopRingbackCadence = (): void => {
      if (ringbackCadenceTimer !== null) {
        clearInterval(ringbackCadenceTimer)
        ringbackCadenceTimer = null
      }
      gainNode.gain.setTargetAtTime(0, audioContext.currentTime, 0.01)
    }
    audio.addEventListener('play', startRingbackCadence)
    audio.addEventListener('pause', stopRingbackCadence)

    audio.srcObject = destination.stream
    ringbackAudioRef.current = audio

    return () => {
      stopRingbackCadence()
      try {
        oscillator1.stop()
        oscillator2.stop()
      } catch {
        // Ignore if already stopped
      }
      audioContext.close()
      ringbackCtxRef.current = null
      audio.remove()
    }
  }, [])

  // Create ringtone (for incoming calls)
  useEffect(() => {
    /*
     * The ring is a real audio FILE — `public/sounds/incoming-ring.mp3`, the
     * same "tring tring" telephone ring the old agent portal played
     * (kcv2-old dialer/assets/incoming.mp3). A file beats the synthesized
     * oscillator approach on every axis that mattered here: exact sound,
     * no AudioContext/cadence bookkeeping, and `loop` gives the cadence
     * baked into the recording.
     *
     * Pause resets it via the callers below; on answer/hangup the element
     * is paused so the file restarts from the beginning on the next call.
     */
    const audio = document.createElement('audio')
    audio.src = '/sounds/incoming-ring.mp3'
    audio.loop = true
    audio.preload = 'auto'
    ringtoneAudioRef.current = audio

    return () => {
      audio.pause()
      ringtoneAudioRef.current = null
      audio.remove()
    }
  }, [])

  // Create busy tone (for call end/hangup)
  useEffect(() => {
    const audio = document.createElement('audio')
    audio.loop = false
    // Generate busy tone using Web Audio API
    const audioContext = new AudioContext()
    busyToneCtxRef.current = audioContext
    const oscillator = audioContext.createOscillator()
    const gainNode = audioContext.createGain()

    oscillator.frequency.value = 480 // Busy tone frequency
    gainNode.gain.value = 0.2

    const destination = audioContext.createMediaStreamDestination()
    oscillator.connect(gainNode)
    gainNode.connect(destination)

    // Start oscillator immediately
    oscillator.start()

    audio.srcObject = destination.stream
    busyToneAudioRef.current = audio

    return () => {
      try {
        oscillator.stop()
      } catch {
        // Ignore if already stopped
      }
      audioContext.close()
      busyToneCtxRef.current = null
      audio.remove()
    }
  }, [])

  /*
   * ONE short beep when a call ends.
   *
   * The previous implementation played a 3s busy tone from BOTH the 'ended'
   * and 'failed' session handlers — JsSIP fires both for a single remote
   * termination, and consult legs attach their own handlers — so each
   * call-cut stacked overlapping play()/pause() windows and the agent heard
   * 2-3 beeps. `lastEndBeepAtRef` collapses every end signal inside one
   * call's teardown into a single 600ms beep.
   */
  const playEndBeep = useCallback(() => {
    const now = Date.now()
    if (now - lastEndBeepAtRef.current < 2000) return
    lastEndBeepAtRef.current = now
    const audio = busyToneAudioRef.current
    if (!audio) return
    const ctx = busyToneCtxRef.current
    if (ctx && ctx.state === 'suspended') {
      void ctx.resume().catch(() => undefined)
    }
    audio.pause()
    audio.play().catch((err: unknown) =>
      console.warn('[useSoftphone] failed to play end-of-call beep:', err)
    )
    setTimeout(() => audio.pause(), 600)
  }, [])

  // Fetch SIP credentials from API on mount (or when config changes)
  useEffect(() => {
    if (!config) return
    let cancelled = false
    const cacheKey = JSON.stringify({
      agentId: config.agentId,
      tenantDomain: config.tenantDomain,
      wsUrl: config.wsUrl,
    })

    console.log('[useSoftphone] fetching SIP credentials for agentId:', config.agentId)
    getOrCreateSipCredential(cacheKey, () => apiGet<SipCreds>('/agent/me/sip-credentials'))
      .then((creds) => {
        console.log('[useSoftphone] SIP credentials fetched successfully:', {
          sipUsername: creds.sipUsername,
          sipDomain: creds.sipDomain,
          hasPassword: !!creds.password,
        })
        if (!cancelled) setSipCreds(creds)
      })
      .catch((err) => {
        console.error('[useSoftphone] failed to fetch SIP credentials:', err)
        // 501 expected until S4; softphone stays unregistered
      })
    return () => {
      cancelled = true
    }
    // Deliberately does NOT reset sipCreds to null on cleanup.
    //
    // THE DEFECT THIS FIXES
    // ----------------------
    // React StrictMode (dev only) mounts every effect twice: mount ->
    // cleanup -> mount. Resetting sipCreds here meant the registration
    // effect below (keyed on sipCreds) tore down its UA on the first
    // cleanup and built a SECOND one on the second mount — before the
    // first UA's WebSocket had necessarily finished closing. Kamailio then
    // registered the alias/port from whichever transport sent the
    // REGISTER, which was not reliably the one the app's single retained
    // UA reference (uaRef.current) actually kept using: `t_relay()` into
    // that agent later failed with "TCP/TLS connection for WebSocket could
    // not be found" on every call, live-reproduced core.tcp_list vs
    // ul.dump port mismatch confirmed the two connections diverged. In
    // production (StrictMode stripped) this two-UA race could not happen,
    // so it never showed up outside dev testing.
    //
    // getOrCreateSipCredential already dedupes the actual fetch via its
    // cache, and `cancelled` above already stops a stale .then() from
    // calling setSipCreds after unmount — nulling the credentials on
    // cleanup added churn without adding correctness.
  }, [config?.agentId, config?.tenantDomain, config?.wsUrl])

  useEffect(() => {
    if (!config || !sipCreds) return

    console.log('[useSoftphone] SIP credentials received:', {
      sipUsername: sipCreds.sipUsername,
      sipDomain: sipCreds.sipDomain,
      sipUri: sipCreds.sipUri,
    })

    const UAConstructor = JsSIP.UA as unknown as new (cfg: UaConfig) => JsSipUaInstance
    // ITEM 5: `useStun` was rendered as a toggle in the agent UI and persisted
    // to localStorage, but no ICE code ever read it. It is now honoured.
    const uaConfig = buildUaConfig(config, sipCreds.password, sipCreds.sipUsername, {
      useStun: useStunRef.current,
    })
    console.log('[useSoftphone] UA config:', {
      uri: uaConfig.uri,
      iceServerCount: uaConfig.pcConfig.iceServers?.length ?? 0,
    })
    pcConfigRef.current = uaConfig.pcConfig
    const ua = new UAConstructor(uaConfig)
    uaRef.current = ua

    const onRegistered = (): void => {
      console.log('[useSoftphone] registered with SIP server as:', sipCreds.sipUsername)
      setRegistered(true)
    }
    const onUnregistered = (): void => {
      console.log('[useSoftphone] unregistered from SIP server')
      setRegistered(false)
    }
    const onRegistrationFailed = (data?: unknown): void => {
      const d = data as { cause?: string; response?: { status_code?: number; reason_phrase?: string } } | undefined
      console.error(
        '[useSoftphone] SIP registration failed —',
        'status:', d?.response?.status_code ?? 'N/A',
        'reason:', d?.response?.reason_phrase ?? d?.cause ?? 'unknown',
        'data:', data,
      )
      setRegistered(false)
    }

    const onNewRTCSession = (...args: unknown[]): void => {
      const data = args[0] as NewRtcSessionEvent
      const session = data.session

      /*
       * ICE GATHERING CAP — do not hold the INVITE waiting for TURN.
       *
       * THE DEFECT THIS FIXES
       * ---------------------
       * JsSIP does not send the INVITE until ICE gathering RESOLVES
       * (RTCSession._createLocalDescription waits on a promise settled either
       * by `icegatheringstatechange === 'complete'` or by the last candidate).
       * Chrome does not report gathering complete until every configured ICE
       * server has answered or timed out, and its TURN timeout is ~10s.
       *
       * Meanwhile the UI starts its LOCAL 440/480Hz ringback the instant the
       * agent clicks dial. So the agent heard 10-15s of ringback for a call
       * that had not left the browser and was not ringing anywhere. Measured
       * on this deployment: Kamailio->Asterisk 250ms, dialplan+AGI 21ms,
       * Asterisk->carrier 183 Session Progress 341ms — roughly 600ms of
       * genuine setup. Essentially the whole delay was gathering.
       *
       * WHY CAPPING IS SAFE
       * -------------------
       * `ready()` makes JsSIP send the offer with the candidates gathered so
       * far; it does NOT cancel the PeerConnection, which keeps gathering and
       * still uses a late TURN candidate if one is needed. Host and
       * server-reflexive candidates arrive in well under this budget, and on
       * this deployment they are what actually carry the media (selected pair
       * observed as host/prflx, with coturn relaying zero bytes). So TURN
       * stays configured as a fallback for restrictive networks without
       * blocking call setup on it.
       *
       * The callback is the SAME shared closure JsSIP passes to every
       * `icecandidate` emit and is guarded by an internal `finished` flag, so
       * invoking it early is idempotent and races harmlessly with natural
       * completion.
       */
      let iceCapTimer: ReturnType<typeof setTimeout> | null = null
      session.on('icecandidate', (...iceArgs: unknown[]) => {
        const ev = iceArgs[0] as { ready?: () => void } | undefined
        if (typeof ev?.ready !== 'function' || iceCapTimer !== null) return
        const ready = ev.ready
        iceCapTimer = setTimeout(() => {
          iceCapTimer = null
          console.log(`[useSoftphone][ice] gathering cap ${ICE_GATHERING_CAP_MS}ms reached — sending offer now`)
          try {
            ready()
          } catch {
            // Gathering finished on its own first; `ready` is a no-op then.
          }
        }, ICE_GATHERING_CAP_MS)
      })

      // ITEM 98 — do not orphan a live call.
      //
      // This assignment was unconditional, so a second INVITE (a consultation
      // leg, or a race between two inbound calls) REPLACED the live session:
      // the previous caller became unreachable — no hangup, no unhold, and the
      // UI no longer knew they existed.
      //
      // The registry now decides. A second session goes into the `consult` slot
      // instead of destroying the first, and `sessionRef` keeps pointing at the
      // primary call so every existing consumer behaves as before.
      const registry = sessionsRef.current
      // Clean up stale terminated sessions in the registry before assigning
      // the new one. Even with the registry-based role detection in the
      // ended/failed handlers, a race could leave a terminated session in a
      // registry slot (e.g. the session terminated between the last event
      // check and now). A stale primary blocks the new call from taking the
      // primary slot — it goes to consult instead, and sessionRef still
      // points at the dead session, so answer() would throw "Invalid
      // status: 8".
      const existingPrimary = registry.get('primary')
      if (existingPrimary && isSessionTerminated(existingPrimary)) {
        console.warn('[useSoftphone] clearing stale terminated primary session from registry')
        registry.clear('primary')
        if (sessionRef.current === existingPrimary) {
          sessionRef.current = null
        }
      }
      const existingConsult = registry.get('consult')
      if (existingConsult && isSessionTerminated(existingConsult)) {
        console.warn('[useSoftphone] clearing stale terminated consult session from registry')
        registry.clear('consult')
      }
      // ITEM 98 follow-up — the 'ended'/'failed' handlers below determine
      // this session's role by checking the registry at event time (not via
      // a captured variable), so a promoted consult leg cleans up correctly
      // when it terminates. See the notes on those handlers for details.
      if (registry.get('primary') === null) {
        registry.set('primary', session)
        sessionRef.current = session
      } else if (registry.get('consult') === null) {
        registry.set('consult', session)
        console.log('[useSoftphone] second session held as consultation leg')
      } else {
        // Both slots occupied: refuse rather than silently drop a live call.
        console.error('[useSoftphone] third concurrent session refused — both legs busy')
      }

      // Set up call log tracking
      // Use agent-specific uniqueId so both sides of a call get separate DB rows
      const baseCallId = (session as any).id ?? `sip-${Date.now()}`
      const agentId = config?.agentId ?? 'unknown'
      const uniqueId = `${baseCallId}:${agentId}`

      // Extract extension number from SIP URI (e.g. "localhost_1002" → "1002")
      const localUri = (session as any).local_identity?.uri?.user ?? ''
      const remoteUri = session.remote_identity?.uri?.user ?? ''
      const localExt = localUri.includes('_') ? localUri.split('_')[1] : localUri
      const remoteExt = remoteUri.includes('_') ? remoteUri.split('_')[1] : remoteUri

      callLogRef.current = {
        uniqueId,
        fromNumber: data.originator === 'remote' ? remoteExt : localExt,
        toNumber: data.originator === 'remote' ? localExt : remoteExt,
        direction: data.originator === 'remote' ? 'INBOUND' : 'OUTBOUND',
        startTime: new Date().toISOString(),
      }
      logCall('start')

      if (data.originator === 'remote') {
        const rawDisplayName = session.remote_identity?.display_name
        const uriUser = session.remote_identity?.uri?.user ?? null
        // Client-portal visitor calls arrive with a From user like
        // `visitor_<callSessionId>_<visitorId>`. Never show that raw SIP
        // username to the agent — the socket's call.incoming already carries
        // the resolved callerDisplayName, and AgentSoftphonePanel falls back to
        // 'Unknown' when neither is available.
        // Some Kamailio configs echo the username back as the display name,
        // so treat any visitor-shaped string as if no display name was sent.
        const isVisitorUri = uriUser !== null && /^(?:visitor|visitor-call)_/.test(uriUser)
        const isVisitorDisplayName = typeof rawDisplayName === 'string' && /^(?:visitor|visitor-call)_/.test(rawDisplayName)
        const isVisitorCall = isVisitorUri || isVisitorDisplayName
        const displayName = rawDisplayName && !isVisitorDisplayName ? rawDisplayName : null
        const friendlyCLI = isVisitorCall ? displayName : uriUser || displayName
        console.log('[useSoftphone] incoming call debug:', {
          rawDisplayName,
          uriUser,
          isVisitorUri,
          isVisitorDisplayName,
          sanitizedDisplayName: displayName,
          friendlyCLI,
        })
        setCallState('ringing_in')
        setRemoteCLI(friendlyCLI)

        // ITEM 99b — correlate the SIP INVITE with the cached `call:incoming`
        // event to populate `activeCall` (header display, screen pop, caller
        // info). `call:incoming` is broadcast to ALL agents in the tenant room,
        // but only THIS agent received the SIP INVITE — so `activeCall` must be
        // set HERE, not on the socket event, or every agent's header would show
        // "Client Portal Caller" even when their softphone never rang.
        // `uriUser` matches `call:incoming.from` for both visitor calls
        // (`visitor_<callSessionId>`) and PSTN calls (E.164 number).
        if (uriUser !== null) {
          const activated = useCallStore.getState().activateFromIncoming(uriUser)
          if (!activated) {
            console.warn('[useSoftphone] no cached call:incoming matched SIP from — activeCall set from SIP URI only', { uriUser })
          }
        }

        // Play ringtone for incoming call. It's a plain <audio> element
        // playing a file — no AudioContext involved — so play() is all that
        // is needed. By this point the agent has interacted with the page
        // (login, "Make Available", echo test), so autoplay policy allows it;
        // a rejection is logged, not fatal.
        if (ringtoneAudioRef.current) {
          ringtoneAudioRef.current.currentTime = 0
          ringtoneAudioRef.current.play().catch(err =>
            console.warn('[useSoftphone] failed to play ringtone:', err)
          )
        }

        // ITEM 99 — auto-answer, if the agent's profile enables it.
        //
        // `hasActiveCall` is the guard that matters: an agent already on a call
        // must NOT have a second one silently answered, which would drop the
        // first caller or conference them without consent. The agent gets no
        // chance to intervene precisely because auto-answer did not ask.
        if (
          shouldAutoAnswer({
            enabled: config.autoAnswerIncomingCalls,
            callState: 'ringing_in',
            hasActiveCall: sessionRef.current !== null && sessionRef.current !== session,
          })
        ) {
          console.log('[useSoftphone] auto-answering incoming call')
          if (ringtoneAudioRef.current) {
            ringtoneAudioRef.current.pause()
          }
          try {
            getCallMediaStream().then((stream) => {
              session.answer({ mediaConstraints: { audio: true, video: false }, pcConfig: pcConfigRef.current, ...(stream ? { mediaStream: stream } : {}) })
            }).catch((err) => {
              console.error('[useSoftphone] auto-answer failed', err)
              session.answer({ mediaConstraints: { audio: true, video: false }, pcConfig: pcConfigRef.current })
            })
          } catch (err) {
            console.error('[useSoftphone] auto-answer failed', err)
          }
        }
      } else {
        console.log('[useSoftphone] outgoing call initiated')
        if (isEchoTestCallRef.current) {
          setRemoteCLI('Echo Test')
        }
      }

      // Play ringback tone when remote is ringing (180 Ringing)
      session.on('progress', () => {
        console.log('[useSoftphone] call progress - remote ringing')
        if (ringbackAudioRef.current) {
          const ctx = ringbackCtxRef.current
          if (ctx && ctx.state === 'suspended') {
            ctx.resume().catch(err =>
              console.warn('[useSoftphone] failed to resume ringback AudioContext:', err)
            )
          }
          ringbackAudioRef.current.play().catch(err =>
            console.warn('[useSoftphone] failed to play ringback:', err)
          )
        }
      })

      session.on('accepted', () => {
        console.log('[useSoftphone] call accepted')
        setCallState('in_call')
        setCallStartTime(Date.now())
        logCall('answer')

        // Stop ringback and ringtone
        ringbackAudioRef.current?.pause()
        ringtoneAudioRef.current?.pause()
        
        // Attach remote audio stream
        const peerConnection = (session as any).connection
        setPeerConnection((peerConnection as RTCPeerConnection | null) ?? null)
        if (peerConnection && remoteAudioRef.current) {
          const remoteStream = new MediaStream()
          peerConnection.getReceivers().forEach((receiver: RTCRtpReceiver) => {
            if (receiver.track) {
              remoteStream.addTrack(receiver.track)
            }
          })
          remoteAudioRef.current.srcObject = remoteStream
          console.log(
            '[useSoftphone] remote audio stream attached — tracks:',
            remoteStream.getTracks().map((t) => `${t.kind}:${t.readyState}`),
          )
        }
        // Diagnostics for "call connects but no audio" — mirrors
        // useClientCall.ts's identical block. The selected candidate pair's
        // local/remote types (host / srflx / relay) is the direct signal:
        // anything failing to produce a succeeded pair means ICE never
        // actually connected despite SDP negotiation completing, and
        // 0 bytesReceived on a connected pair means the far end (Kamailio's
        // RTPEngine relay, or the visitor's own leg) isn't sending RTP here.
        if (peerConnection) {
          const pc = peerConnection as RTCPeerConnection
          pc.addEventListener('iceconnectionstatechange', () => {
            console.log('[useSoftphone][webrtc] iceConnectionState:', pc.iceConnectionState)
          })
          pc.addEventListener('track', (ev) => {
            console.log('[useSoftphone][webrtc] remote track received:', ev.track.kind, 'readyState:', ev.track.readyState, 'muted:', ev.track.muted)
          })
          setTimeout(() => {
            void pc.getStats().then((stats) => {
              let selectedPairId: string | undefined
              stats.forEach((report) => {
                if (report.type === 'transport' && typeof report.selectedCandidatePairId === 'string') {
                  selectedPairId = report.selectedCandidatePairId
                }
              })
              if (!selectedPairId) {
                console.warn('[useSoftphone][webrtc] getStats: no selected candidate pair — ICE never connected')
                return
              }
              const pair = stats.get(selectedPairId) as
                | { localCandidateId?: string; remoteCandidateId?: string; state?: string; bytesReceived?: number; bytesSent?: number }
                | undefined
              const local = pair?.localCandidateId ? (stats.get(pair.localCandidateId) as { candidateType?: string } | undefined) : undefined
              const remote = pair?.remoteCandidateId ? (stats.get(pair.remoteCandidateId) as { candidateType?: string } | undefined) : undefined
              console.log('[useSoftphone][webrtc] getStats: selected pair', {
                state: pair?.state,
                localType: local?.candidateType,
                remoteType: remote?.candidateType,
                bytesReceived: pair?.bytesReceived,
                bytesSent: pair?.bytesSent,
              })
              if ((pair?.bytesReceived ?? 0) === 0) {
                console.warn('[useSoftphone][webrtc] getStats: 0 bytes received on the selected pair — ICE connected but no inbound RTP is arriving')
              }
            })
          }, 3000)
        }
      })
      session.on('confirmed', () => {
        console.log('[useSoftphone] call confirmed')
        setCallState('in_call')
        
        // Stop ringback and ringtone
        ringbackAudioRef.current?.pause()
        ringtoneAudioRef.current?.pause()
        
        // Also attach on confirmed in case accepted didn't fire
        const peerConnection = (session as any).connection
        setPeerConnection((peerConnection as RTCPeerConnection | null) ?? null)
        if (peerConnection && remoteAudioRef.current && !remoteAudioRef.current.srcObject) {
          const remoteStream = new MediaStream()
          peerConnection.getReceivers().forEach((receiver: RTCRtpReceiver) => {
            if (receiver.track) {
              remoteStream.addTrack(receiver.track)
            }
          })
          remoteAudioRef.current.srcObject = remoteStream
          console.log('[useSoftphone] remote audio stream attached on confirmed')
        }
      })
      // ITEM 105 — the SIP result, not the request, drives the UI state.
      session.on('hold', () => {
        console.log('[useSoftphone] hold confirmed by remote')
        setCallState('on_hold')
      })

      session.on('unhold', () => {
        console.log('[useSoftphone] unhold confirmed by remote')
        setCallState('in_call')
      })

      // A rejected re-INVITE leaves the call ACTIVE. Staying in_call is the
      // correct outcome, but it must be a deliberate decision rather than the
      // accident of never having listened — and it is logged, because an agent
      // pressing hold and staying live needs to be explicable afterwards.
      session.on('holdFailed', () => {
        console.error('[useSoftphone] hold failed — call remains ACTIVE, audio still flowing')
        setCallState('in_call')
      })

      session.on('unholdFailed', () => {
        console.error('[useSoftphone] unhold failed — call remains on hold')
        setCallState('on_hold')
      })

      session.on('ended', () => {
        // Determine this session's CURRENT role from the registry, not from
        // the role captured at creation time (ownRole). A consult leg that
        // was promoted to primary when the original caller hung up still has
        // ownRole='consult' in this closure — using that would trigger the
        // early return below, leaving sessionRef pointing at a terminated
        // session. The next incoming call would then set callState='ringing_in'
        // while sessionRef holds a dead session, and answer() would throw
        // "Invalid status: 8".
        const registry = sessionsRef.current
        const isCurrentConsult = registry.get('consult') === session
        const isCurrentPrimary = registry.get('primary') === session

        console.log('[useSoftphone] call ended — isPrimary:', isCurrentPrimary, 'isConsult:', isCurrentConsult)

        // This handler is bound once per session — primary AND consult each
        // get their own copy. It used to unconditionally clear('primary') and
        // reset the whole call UI to idle regardless of which leg actually
        // ended. That meant a CONSULTATION leg ending (target didn't answer,
        // agent cancelled, target hung up) wiped out the still-live primary
        // call's registry entry and dropped the agent's UI to idle out from
        // under a caller who was simply on hold — and the next transfer
        // attempt saw registry.get('primary') === null and refused with
        // "no active call to transfer" even though the caller was still on
        // the line. Only the branch matching this session's current role may
        // touch the primary call's state.
        if (isCurrentConsult && !isCurrentPrimary) {
          registry.clear('consult')
          console.log('[useSoftphone] consultation leg ended — primary call unaffected')
          return
        }

        // Stop all tones
        ringbackAudioRef.current?.pause()
        ringtoneAudioRef.current?.pause()

        // One beep for the call ending.
        playEndBeep()

        // Echo test completed successfully
        if (isEchoTestCallRef.current) {
          setEchoTestPassed(true)
          setEchoTestRunning(false)
          isEchoTestCallRef.current = false
        }

        logCall('end')
        setCallState('idle')
        setRemoteCLI(null)
        setIsMuted(false)
        setCallStartTime(null)
        sessionRef.current = null
        // ITEM 98 — keep the registry in step, and promote a consultation leg
        // if one is live: the caller hung up while the agent was consulting, and
        // orphaning that session would leave the agent talking on a call the UI
        // does not know about.
        sessionsRef.current.clear('primary')
        const promoted = sessionsRef.current.planPrimaryLost().promote
        if (promoted) {
          sessionsRef.current.clear('consult')
          sessionsRef.current.set('primary', promoted)
          sessionRef.current = promoted as JsSipSession
        } else {
          // Ticket creation from a call now happens only through the softphone
          // panel's Create Ticket button (CallWorkDrawer); no post-call dialog.
          releaseSharedCallState()
        }
      setPeerConnection(null)
        // ITEM 100 — a stale connection keeps getStats() polling a CLOSED peer
        // connection: it throws on some browsers and reports a frozen MOS on
        // others, so the meter shows the previous call's score during the next.
        setPeerConnection(null)
      })
      // JsSIP's 'failed' event with cause WEBRTC_ERROR only carries the cause
      // string, not the underlying DOMException — setRemoteDescription /
      // getUserMedia rejections are swallowed into that one generic cause
      // (RTCSession.js: request.reply(...); this._failed('system', null,
      // JsSIP_C.causes.WEBRTC_ERROR); throw new Error(...)). These two events
      // fire immediately before 'failed' and carry the real browser error.
      session.on('peerconnection:setremotedescriptionfailed', (error?: unknown) => {
        console.error('[useSoftphone] setRemoteDescription failed — the SDP Asterisk sent was rejected by the browser:', error)
      })
      session.on('getusermediafailed', (error?: unknown) => {
        console.error('[useSoftphone] getUserMedia failed:', error)
      })
      // TSV-03-0008 — ICE failure must terminate the SIP dialog instead of
      // leaving a zombie call (SIP 200 OK, UI in_call, no media path). JsSIP
      // only terminates on iceConnectionState='failed', which never fires
      // when ZERO candidates were gathered (no pairs to check -> state sits
      // at 'new'). Outbound PCs already exist here — 'peerconnection' was
      // emitted inside ua.call() before this handler ran — so the event
      // listener alone would miss them; attach to session.connection too.
      {
        let iceGuardAttached = false
        const attachIceGuard = (pc?: RTCPeerConnection | null) => {
          if (!pc || iceGuardAttached) return
          iceGuardAttached = true
          attachIceFailureGuard(session as unknown as { terminate: () => void }, pc, {
            onTerminate: (reason) =>
              console.error('[useSoftphone] ICE failure — terminating call:', reason),
          })
        }
        attachIceGuard((session as any).connection as RTCPeerConnection | null)
        session.on('peerconnection', (data?: unknown) => {
          attachIceGuard((data as { peerconnection?: RTCPeerConnection } | undefined)?.peerconnection)
        })
      }
      session.on('failed', (data?: unknown) => {
        const failureData = data as any
        const cause = failureData?.cause ?? 'Unknown'
        const statusCode = failureData?.message?.status ?? 'Unknown'
        const message = failureData?.message?.reason ?? failureData?.message?.data ?? ''
        
        console.warn('[useSoftphone] call failed', {
          cause,
          statusCode,
          message,
        })
        
        // Log specific failure reasons with actionable messages
        if (cause === 'Not Found' || statusCode === 404 || message.includes('404')) {
          console.error('[useSoftphone] call failed: Agent not found or not currently registered')
          console.error('[useSoftphone] Possible reasons:')
          console.error('[useSoftphone]   - Agent is offline/not logged in')
          console.error('[useSoftphone]   - Extension does not exist')
          console.error('[useSoftphone]   - Agent has not registered their softphone')
        } else if (statusCode === 480 || message.includes('480')) {
          console.error('[useSoftphone] call failed: Agent temporarily unavailable')
        } else if (statusCode === 486 || message.includes('486')) {
          console.error('[useSoftphone] call failed: Agent busy')
        } else if (statusCode === 487 || message.includes('487')) {
          console.error('[useSoftphone] call failed: Call request terminated')
        } else {
          console.error('[useSoftphone] call failed with cause:', cause)
        }
        
        logCall('failed', { cause, statusCode })

        // Same per-session-role guard as 'ended' above, but using the
        // registry-based role detection (not the captured ownRole) for the
        // same reason: a promoted consult leg still has ownRole='consult'
        // in this closure and would return early without cleaning up.
        // This is the MORE common way to hit it in practice: the agent
        // starts a consultation and the transfer target is
        // unreachable/busy/declines (exactly the 404/480/486/487 causes
        // logged above) — that is a 'failed' event on the CONSULT session,
        // not the primary. Without this guard it unconditionally cleared
        // the primary call's registry entry and reset the UI to idle,
        // hanging up on the original caller who was simply on hold, and
        // leaving the next transfer attempt unable to find a primary
        // session at all.
        const failRegistry = sessionsRef.current
        const failIsConsult = failRegistry.get('consult') === session
        const failIsPrimary = failRegistry.get('primary') === session
        if (failIsConsult && !failIsPrimary) {
          failRegistry.clear('consult')
          console.log('[useSoftphone] consultation leg failed — primary call unaffected')
          return
        }

        // Stop all tones
        ringbackAudioRef.current?.pause()
        ringtoneAudioRef.current?.pause()

        // One beep for the call failing/ending.
        playEndBeep()

        // Echo test failed / stopped (SIP mode only — local loopback never reaches here)
        if (isEchoTestCallRef.current) {
          setEchoTestRunning(false)
          isEchoTestCallRef.current = false
        }

        setCallState('idle')
        setRemoteCLI(null)
        setIsMuted(false)
        setCallStartTime(null)
        sessionRef.current = null
        // ITEM 98 — keep the registry in step, and promote a consultation leg
        // if one is live: the caller hung up while the agent was consulting, and
        // orphaning that session would leave the agent talking on a call the UI
        // does not know about.
        sessionsRef.current.clear('primary')
        const promoted = sessionsRef.current.planPrimaryLost().promote
        if (promoted) {
          sessionsRef.current.clear('consult')
          sessionsRef.current.set('primary', promoted)
          sessionRef.current = promoted as JsSipSession
        } else {
          releaseSharedCallState()
        }
        // ITEM 100 — a stale connection keeps getStats() polling a CLOSED peer
        // connection: it throws on some browsers and reports a frozen MOS on
        // others, so the meter shows the previous call's score during the next.
        setPeerConnection(null)
      })

      // Handle incoming REFER (call transfer) on this session
      session.on('refer', (data?: unknown) => {
        console.log('[useSoftphone] incoming REFER on session')
        const referData = data as any
        const referTo = referData?.refer_to || referData?.request?.getHeader('Refer-To')
        
        console.log('[useSoftphone] REFER Refer-To:', referTo)
        
        if (!referTo) {
          console.warn('[useSoftphone] REFER has no Refer-To header')
          return
        }
        
        // Extract SIP URI from Refer-To header (remove < > if present)
        let targetUri = referTo.toString()
        if (targetUri.startsWith('<')) {
          targetUri = targetUri.substring(1, targetUri.length - 1)
        }
        
        console.log('[useSoftphone] REFER accepted - will call', targetUri)
        
        // Terminate the current call
        session.terminate()
        console.log('[useSoftphone] current call terminated for transfer')
        
        // Automatically call the transfer target after a brief delay
        setTimeout(() => {
          console.log('[useSoftphone] initiating call to transfer target:', targetUri)
          if (ua) {
            try {
              ua.call(targetUri, { mediaConstraints: { audio: true, video: false }, pcConfig: pcConfigRef.current })
              setCallState('ringing_out')
            } catch (err) {
              console.error('[useSoftphone] failed to call transfer target:', err)
            }
          }
        }, 500)
      })
    }

    // Handle incoming REFER requests (call transfer)
    const onNewReferral = (...args: unknown[]): void => {
      const data = args[0] as any
      console.log('[useSoftphone] incoming REFER request')
      console.log('[useSoftphone] Refer-To:', data.refer_to)
      
      // Extract the target URI from Refer-To header
      const referTo = data.refer_to
      if (!referTo) {
        console.warn('[useSoftphone] REFER has no Refer-To header')
        return
      }
      
      // Accept the REFER
      data.accept()
      console.log('[useSoftphone] REFER accepted - will call', referTo)
      
      // Terminate the current call
      if (sessionRef.current) {
        sessionRef.current.terminate()
        console.log('[useSoftphone] current call terminated for transfer')
      }
      
      // Automatically call the transfer target
      setTimeout(() => {
        console.log('[useSoftphone] initiating call to transfer target:', referTo)
        if (ua) {
          try {
            ua.call(referTo, { mediaConstraints: { audio: true, video: false }, pcConfig: pcConfigRef.current })
            setCallState('ringing_out')
          } catch (err) {
            console.error('[useSoftphone] failed to call transfer target:', err)
          }
        }
      }, 500)
    }

    ua.on('registered', onRegistered)
    ua.on('unregistered', onUnregistered)
    ua.on('registrationFailed', onRegistrationFailed)
    ua.on('newRTCSession', onNewRTCSession)
    ua.on('newReferral', onNewReferral)
    // Transport debug — track WebSocket connect/disconnect to diagnose
    // cases where the WS handshake completes but no REGISTER follows.
    ua.on('connected', () => {
      console.log('[useSoftphone] WebSocket transport connected to:', config.wsUrl)
    })
    ua.on('disconnected', (data?: unknown) => {
      const d = data as { code?: number; reason?: string } | undefined
      console.warn('[useSoftphone] WebSocket transport disconnected — code:', d?.code, 'reason:', d?.reason)
    })

    console.log('[useSoftphone] starting UA with wsUrl:', config.wsUrl)
    ua.start()

    /*
     * RFC 7118 §4 CRLF keep-alive.
     *
     * JsSIP ships no WS keepalive: on an idle desk the socket carries zero
     * traffic between REGISTER refreshes, and any hop that drops idle
     * connections (NAT, Kamailio's `websocket keepalive_timeout`, an upstream
     * proxy) kills it — observed live as `disconnected code:1006` minutes
     * after a perfectly good registration, with the agent then uncallable.
     * A double-CRLF ping is the SIP-over-WS convention; the server answers
     * with CRLF and the connection stays warm at every layer.
     *
     * `uaConfig.sockets[0]` is the live WebSocketInterface — JsSIP exposes
     * send()/isConnected() on it. The try/catch is deliberate: a send racing
     * transport teardown must not take the interval (or the UA) down.
     */
    const sipSocket = uaConfig.sockets[0] as
      | { isConnected(): boolean; send(message: string): boolean }
      | undefined
    const sipKeepalive = setInterval(() => {
      try {
        if (sipSocket?.isConnected()) sipSocket.send('\r\n\r\n')
      } catch {
        // transport teardown raced the ping — JsSIP's reconnect owns recovery
      }
    }, 30_000)

    // TSV-03 tab close/refresh teardown: terminate active sessions BEFORE
    // the UA stops. Without this, closing/refreshing a tab while a call was
    // ringing or connected left the remote side in-call with no BYE/CANCEL
    // — the callee stayed ringing_in 30s+ until SIP timer expiry, or stayed
    // in_call indefinitely. ua.stop() only de-registers; it does NOT send
    // BYE/CANCEL for in-progress sessions.
    const teardownAllSessions = () => {
      for (const sess of sessionsRef.current.all()) {
        try {
          (sess as { terminate?: () => void }).terminate?.()
        } catch {
          // session may already be terminated
        }
      }
      try {
        sessionRef.current?.terminate()
      } catch {
        // already terminated
      }
    }
    const onBeforeUnload = () => {
      teardownAllSessions()
      try { ua.stop() } catch { /* already stopped */ }
    }
    /*
     * `pagehide` does NOT only mean "the tab is closing".
     *
     * THE DEFECT THIS FIXES
     * ---------------------
     * It also fires when the page enters the back/forward cache, where the
     * tab is very much alive and will usually be restored. Tearing the UA
     * down there killed SIP registration for the REST OF THE TAB'S LIFE:
     * React does not remount on a bfcache restore, so this effect never
     * re-ran and nothing ever called `ua.start()` again. Observed live —
     * an open tab holding a live WebSocket with ZERO REGISTERs reaching
     * Kamailio, the agent permanently unregistered (grey icon, uncallable)
     * until a manual reload.
     *
     * `persisted` is the documented discriminator: true = bfcache, leave the
     * UA alone; false = a genuine teardown, same as `beforeunload`. That
     * keeps the original fix (no orphaned calls on close/refresh) without
     * silently unregistering a tab that is coming straight back.
     */
    const onPageHide = (ev: PageTransitionEvent) => {
      if (ev.persisted) return
      onBeforeUnload()
    }
    /*
     * Restored from bfcache. The JS heap survived but the WebSocket did not,
     * so JsSIP is holding a dead transport and will not re-register on its
     * own. Restart it rather than leave the agent silently unregistered.
     */
    const onPageShow = (ev: PageTransitionEvent) => {
      if (!ev.persisted) return
      try { ua.start() } catch { /* already started */ }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    window.addEventListener('pagehide', onPageHide)
    window.addEventListener('pageshow', onPageShow)

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      window.removeEventListener('pagehide', onPageHide)
      window.removeEventListener('pageshow', onPageShow)
      clearInterval(sipKeepalive)
      teardownAllSessions()
      ua.stop()
      uaRef.current = null
      sessionRef.current = null
    }
  }, [config?.agentId, config?.tenantDomain, config?.wsUrl, sipCreds])

  const answer = useCallback(async () => {
    const session = sessionRef.current
    if (!session) {
      console.warn('[useSoftphone] answer() called but no active session')
      return
    }
    // JsSIP RTCSession status: 4 = STATUS_WAITING_FOR_ANSWER (the only
    // valid state for answer()), 8 = STATUS_TERMINATED. Calling answer()
    // on a terminated session throws INVALID_STATE_ERROR — this happens
    // when the session has ended but the UI still shows 'ringing_in',
    // e.g. a promoted consult leg whose 'ended' handler returned early
    // (ownRole === 'consult') without clearing sessionRef, followed by a
    // new incoming call that set callState='ringing_in'.
    const status = (session as { status?: number }).status
    if (status !== undefined && status !== 4) {
      console.warn('[useSoftphone] answer() called but session status is', status, '— resetting stale call state')
      setCallState('idle')
      setRemoteCLI(null)
      setIsMuted(false)
      setCallStartTime(null)
      sessionRef.current = null
      sessionsRef.current.clear('primary')
      sessionsRef.current.clear('consult')
      releaseSharedCallState()
      return
    }
    try {
      const stream = await getCallMediaStream()
      session.answer({ mediaConstraints: { audio: true, video: false }, pcConfig: pcConfigRef.current, ...(stream ? { mediaStream: stream } : {}) })
    } catch (err) {
      console.error('[useSoftphone] answer() failed:', err)
      setCallState('idle')
      setRemoteCLI(null)
      setIsMuted(false)
      setCallStartTime(null)
      sessionRef.current = null
      sessionsRef.current.clear('primary')
      sessionsRef.current.clear('consult')
      releaseSharedCallState()
    }
  }, [])

  const hangup = useCallback(() => {
    const allLegs = sessionsRef.current.all() as JsSipSession[]
    for (const leg of allLegs) {
      try {
        leg?.terminate()
      } catch (err) {
        console.warn('[useSoftphone] error terminating session leg:', err)
      }
    }
    if (sessionRef.current && !allLegs.some((l) => l === sessionRef.current)) {
      try {
        sessionRef.current.terminate()
      } catch (err) {
        console.warn('[useSoftphone] error terminating primary session:', err)
      }
    }
  }, [])

  /**
   * Lightweight call logging to POST /calls/log.
   * Writes a call row so AgentCallsPage can show recent activity.
   */
  const logCall = useCallback(
    async (
      type: 'start' | 'answer' | 'end' | 'failed',
      opts: { cause?: string; statusCode?: string | number } = {},
    ) => {
      const logData = callLogRef.current
      if (!logData) return
      if (isEchoTestCallRef.current) return // Don't log echo test

      const now = new Date().toISOString()
      const payload: Record<string, unknown> = {
        uniqueId: logData.uniqueId,
        fromNumber: logData.fromNumber,
        toNumber: logData.toNumber,
        direction: logData.direction,
        startTime: logData.startTime,
        agentId: agentIdRef.current,
      }

      if (type === 'start') {
        // Initial insert
      } else if (type === 'answer') {
        payload.answerTime = now
      } else if (type === 'end' || type === 'failed') {
        payload.endTime = now
        const startMs = timestampMs(logData.startTime)
        const durationSec = Math.max(0, Math.round((Date.now() - startMs) / 1000))
        payload.durationSec = durationSec
        payload.disposition = type === 'failed' ? 'failed' : 'answered'
        if (opts.cause) payload.hangupCause = opts.cause
      }

      console.log('[useSoftphone] logging call:', type, payload)
      try {
        await apiPost('/calls/log', payload)
        console.log('[useSoftphone] call logged:', type, logData.uniqueId)
      } catch (err) {
        console.warn('[useSoftphone] call log failed:', err)
      }
    },
    [],
  )

  // ITEM 105 — REQUEST the hold; let the SIP result set the state.
  //
  // These previously set callState('on_hold') / ('in_call') UNCONDITIONALLY,
  // before JsSIP had confirmed the re-INVITE. When the far end rejects it — or
  // it times out, which happens on NAT-bound trunks — the UI showed "on hold"
  // while audio kept flowing, so the agent believed the caller could not hear
  // them and spoke accordingly.
  //
  // JsSIP emits 'hold' and 'unhold' on the session once the operation is
  // CONFIRMED. Those handlers (registered with the other session events) are now
  // the only thing that moves the state.
  const hold = useCallback(() => {
    sessionRef.current?.hold?.()
  }, [])

  const unhold = useCallback(() => {
    sessionRef.current?.unhold?.()
  }, [])

  const resumeForTransfer = useCallback(async (): Promise<void> => {
    const session = sessionRef.current
    if (!session || isSessionTerminated(session)) throw new Error('The call is no longer active.')
    if (!(session.isOnHold?.().local ?? (callState === 'on_hold'))) return
    if (!session.unhold) throw new Error('Resume the call before transferring.')
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Could not resume the call. Resume it before retrying transfer.')), 8000)
      try {
        const started = session.unhold!({}, () => { clearTimeout(timer); resolve() })
        if (started === false) { clearTimeout(timer); reject(new Error('The call is busy renegotiating. Try again shortly.')) }
      } catch (error) { clearTimeout(timer); reject(error) }
    })
  }, [callState])

  const mute = useCallback(() => {
    const session = sessionRef.current
    if (!session) return
    
    if (session.mute) {
      session.mute({ audio: true })
      setIsMuted(true)
      console.log('[useSoftphone] audio muted')
    } else {
      console.warn('[useSoftphone] mute not supported by session')
    }
  }, [])

  const unmute = useCallback(() => {
    const session = sessionRef.current
    if (!session) return
    
    if (session.unmute) {
      session.unmute({ audio: true })
      setIsMuted(false)
      console.log('[useSoftphone] audio unmuted')
    } else {
      console.warn('[useSoftphone] unmute not supported by session')
    }
  }, [])

  const toggleMute = useCallback(() => {
    if (isMuted) {
      unmute()
    } else {
      mute()
    }
  }, [isMuted, mute, unmute])

  const sendDtmf = useCallback((tone: string): boolean => {
    const session = sessionRef.current
    if (!session?.sendDTMF || isSessionTerminated(session) || !/^[0-9*#A-D]+$/i.test(tone)) return false
    try {
      const sender = session.connection?.getSenders().find((s) => s.track?.kind === 'audio')
      session.sendDTMF(tone, {
        transportType: sender?.dtmf?.canInsertDTMF ? 'RFC2833' : 'INFO',
        duration: 160,
        interToneGap: 100,
      })
      return true
    } catch {
      console.warn('[useSoftphone] DTMF could not be sent in the current media/session state')
      return false
    }
  }, [])

  /*
   * ITEM 98 — ATTENDED transfer, as distinct from the blind one below.
   *
   * `transfer()` does `hold()` then `refer()` under a comment reading
   * "Attended transfer workflow". That is a BLIND transfer with a hold in
   * front of it: by the time the agent discovers the target is at lunch the
   * caller has already been handed over and there is no way back.
   *
   * Attended means consult FIRST, then either complete or cancel and return
   * to the caller. `session-registry.ts` already landed the safety half (two
   * concurrent legs no longer orphan each other); this is the feature half.
   *
   * The ORDER of SIP actions lives in `attended-transfer.ts` so it is testable
   * without a SIP stack — that order is where live calls get dropped.
   */
  const attendedRef = useRef(
    createAttendedTransfer({
      registry: sessionsRef.current,
      hold: (sess) => (sess as { hold?: () => void }).hold?.(),
      unhold: (sess) => (sess as { unhold?: () => void }).unhold?.(),
      terminate: (sess) => (sess as { terminate?: () => void }).terminate?.(),
      // `replaces` is the consultation SESSION, not a string — JsSIP
      // refer(target, {replaces}) sends Refer-To with an RFC 3891 Replaces
      // param so the callee joins the consultation dialog instead of
      // ringing a fresh call.
      refer: (sess, replaces) => {
        const remoteUri = (
          replaces as { remote_identity?: { uri?: { toString?: () => string } } }
        ).remote_identity?.uri?.toString?.()
        if (!remoteUri) return
        ;(sess as { refer?: (t: string, o?: { replaces?: unknown }) => unknown }).refer?.(
          remoteUri,
          { replaces },
        )
      },
      placeCall: (target: string) => {
        const ua = uaRef.current
        if (!ua) throw new Error('UA not initialized')
        return ua.call(buildTargetUri(target, config?.tenantDomain), {
          mediaConstraints: { audio: true, video: false },
          pcConfig: pcConfigRef.current,
        }) as unknown as object
      },
    }),
  )

  const startConsultation = useCallback(
    (target: string) => {
      // The registry's `primary` slot is normally filed by the newRTCSession
      // handler, but a session restored mid-flight (or one whose registry
      // entry was cleared by a stale-cleanup path) leaves `sessionRef` live
      // while the slot is empty — which surfaced as "no active call to
      // transfer". Refile it before asking the controller.
      const registry = sessionsRef.current
      if (
        registry.get('primary') === null &&
        sessionRef.current &&
        !isSessionTerminated(sessionRef.current)
      ) {
        registry.set('primary', sessionRef.current)
      }
      return attendedRef.current.startConsultation(target)
    },
    [],
  )
  const cancelConsultation = useCallback(
    () => attendedRef.current.cancelConsultation(),
    [],
  )
  const completeAttendedTransfer = useCallback(
    () => attendedRef.current.completeTransfer(),
    [],
  )

  /** BLIND transfer — hold + REFER, no consultation leg. */
  const transfer = useCallback((target: string) => {
    const session = sessionRef.current
    const ua = uaRef.current

    if (!session) {
      console.warn('[useSoftphone] transfer: no active session')
      return
    }
    
    if (!ua) {
      console.warn('[useSoftphone] transfer: UA not initialized')
      return
    }
    
    // Construct target URI
    let targetUri: string
    if (target.includes('@')) {
      targetUri = `sip:${target}`
    } else if (/^\d+$/.test(target)) {
      const tenantSlug = config?.tenantDomain?.split('.')[0] ?? 'localhost'
      const targetUsername = `${tenantSlug}_${target}`
      targetUri = `sip:${targetUsername}@${config?.tenantDomain ?? ''}`
    } else {
      targetUri = `sip:${target}@${config?.tenantDomain ?? ''}`
    }
    
    console.log('[useSoftphone] transferring call to', targetUri)
    
    // Attended transfer workflow:
    // 1. Put current call on hold (optional but recommended)
    // 2. Send REFER to transfer the call
    // 3. Terminate our session after REFER is accepted
    
    const sessionAny = session as any
    
    if (typeof sessionAny.refer === 'function') {
      try {
        // Put current call on hold before transferring
        if (session.hold) {
          session.hold()
          console.log('[useSoftphone] call put on hold for transfer')
        }
        
        // Send REFER to transfer the call
        // The REFER tells the other party to call the transfer target
        sessionAny.refer(targetUri, {
          eventHandlers: {
            requestSucceeded: () => {
              console.log('[useSoftphone] REFER accepted - transfer in progress')
            },
            requestFailed: () => {
              console.error('[useSoftphone] REFER rejected - transfer failed')
              // Unhold the call if transfer failed
              if (session.unhold) {
                session.unhold()
              }
            },
            trying: () => {
              console.log('[useSoftphone] REFER trying...')
            },
            progress: () => {
              console.log('[useSoftphone] REFER progress...')
            },
            accepted: () => {
              console.log('[useSoftphone] REFER accepted - terminating our session')
              // Transfer successful, we can hang up
              session.terminate()
            },
            failed: () => {
              console.error('[useSoftphone] REFER failed')
              // Unhold the call if transfer failed
              if (session.unhold) {
                session.unhold()
              }
            }
          }
        })
        
        console.log('[useSoftphone] REFER sent to', targetUri)
      } catch (err) {
        console.error('[useSoftphone] REFER transfer failed:', err)
        // Unhold the call if transfer failed
        if (session.unhold) {
          session.unhold()
        }
      }
    } else {
      console.warn('[useSoftphone] REFER not supported - transfer not available')
      console.log('[useSoftphone] Please upgrade to a SIP client that supports REFER for call transfer')
    }
  }, [config?.tenantDomain])

  const dial = useCallback(
    async (target: string) => {
      const ua = uaRef.current
      if (!ua) {
        console.warn('[useSoftphone] dial: UA not initialized')
        return
      }
      if (!registered) {
        console.warn('[useSoftphone] dial: not registered with SIP server')
        return
      }
      
      let targetUri: string
      
      try {
        if (target.includes('@')) {
          // Already a full SIP address (e.g., "user@domain")
          targetUri = `sip:${target}`
          console.log('[useSoftphone] dial: full SIP address provided', targetUri)
        } else if (/^(\+|011|00)[0-9]{10,}$/.test(target) || /^[0-9]{10,}$/.test(target)) {
          // PSTN phone number — route without tenant slug prefix so Kamailio
          // detects it as PSTN and routes to the SIP trunk
          targetUri = `sip:${target}@${config?.tenantDomain ?? ''}`
          console.log('[useSoftphone] dial: PSTN number detected', targetUri)
        } else if (/^\d+$/.test(target)) {
          // Extension number - construct SIP username directly from extension
          // Format: tenantslug_extension (e.g., localhost_1001)
          console.log('[useSoftphone] dial: extension detected, constructing SIP username for extension', target)
          try {
            // Extract tenant slug from domain (e.g., "localhost.ccs.internal" -> "localhost")
            const tenantSlug = config?.tenantDomain?.split('.')[0] ?? 'localhost'
            const targetUsername = `${tenantSlug}_${target}`
            targetUri = `sip:${targetUsername}@${config?.tenantDomain ?? ''}`
            console.log('[useSoftphone] dial: extension', target, 'converted to SIP username', targetUsername)
          } catch (err) {
            console.error('[useSoftphone] dial: failed to resolve extension', target, err)
            setCallState('idle')
            // Check if it's a 404 (extension not found) or other error
            const errorMsg = err instanceof Error ? err.message : String(err)
            if (errorMsg.includes('404') || errorMsg.includes('not found')) {
              console.error('[useSoftphone] dial: extension', target, 'not found in system')
            } else {
              console.error('[useSoftphone] dial: error resolving extension:', errorMsg)
            }
            return
          }
        } else {
          // Assume it's a username or partial SIP address
          targetUri = `sip:${target}@${config?.tenantDomain ?? ''}`
          console.log('[useSoftphone] dial: username provided', targetUri)
        }
        
        console.log('[useSoftphone] dial: initiating call to', targetUri)
        const session = ua.call(targetUri, { mediaConstraints: { audio: true, video: false }, pcConfig: pcConfigRef.current })
        setCallState('ringing_out')
        setRemoteCLI(target)
        console.log('[useSoftphone] dial: call initiated successfully, session created')
        
        // Add timeout to detect if call fails immediately
        const timeoutId = setTimeout(() => {
          if (sessionRef.current === session) {
            console.warn('[useSoftphone] dial: call timeout - no response from target')
          }
        }, 30000) // 30 second timeout
        
        // Clear timeout if call ends
        session.on('ended', () => clearTimeout(timeoutId))
        session.on('failed', () => clearTimeout(timeoutId))
      } catch (err) {
        console.error('[useSoftphone] dial: call initiation failed', err)
        setCallState('idle')
      }
    },
    [registered, config?.tenantDomain],
  )

  // Refresh available audio devices
  const refreshDevices = useCallback(async () => {
    try {
      // Request permission first so labels are available
      await navigator.mediaDevices.getUserMedia({ audio: true })
      const devices = await navigator.mediaDevices.enumerateDevices()
      const inputs = devices
        .filter((d) => d.kind === 'audioinput')
        .map((d) => ({ deviceId: d.deviceId, label: d.label || `Microphone ${d.deviceId.slice(0, 8)}...`, kind: 'audioinput' as const }))
      const outputs = devices
        .filter((d) => d.kind === 'audiooutput')
        .map((d) => ({ deviceId: d.deviceId, label: d.label || `Speaker ${d.deviceId.slice(0, 8)}...`, kind: 'audiooutput' as const }))
      setAudioInputDevices(inputs)
      setAudioOutputDevices(outputs)
      if (!selectedAudioInput && inputs[0]) setSelectedAudioInput(inputs[0].deviceId)
      if (!selectedAudioOutput && outputs[0]) setSelectedAudioOutput(outputs[0].deviceId)
      console.log('[useSoftphone] devices refreshed:', { inputs: inputs.length, outputs: outputs.length })
    } catch (err) {
      console.error('[useSoftphone] failed to enumerate devices:', err)
    }
  }, [selectedAudioInput, selectedAudioOutput])

  // Run echo test: local audio loopback (no SIP server needed)
  const runEchoTest = useCallback(async () => {
    if (echoTestRunning) return
    setEchoTestRunning(true)
    setEchoTestPassed(false)
    isEchoTestCallRef.current = true
    console.log('[useSoftphone] echo test: starting local audio loopback')

    try {
      // Get microphone stream with user-selected telephony options
      const audioConstraints: MediaTrackConstraints = {
        ...(selectedAudioInput ? { deviceId: { exact: selectedAudioInput } } : {}),
        channelCount: useStereo ? 2 : 1,
        echoCancellation: enableEchoCancel,
        noiseSuppression: enableNoiseSupp,
        autoGainControl: enableAgc,
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
        video: false,
      })
      echoTestStreamRef.current = stream

      // Connect mic stream to remote audio element so user hears themselves
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = stream
        remoteAudioRef.current.play().catch((err) =>
          console.warn('[useSoftphone] echo test: audio play failed:', err)
        )
      }

      // Show as "in call" immediately — no ringing, no server
      setCallState('in_call')
      setCallStartTime(Date.now())
      setRemoteCLI('Echo Test')
      console.log('[useSoftphone] echo test: local loopback active')
    } catch (err) {
      console.error('[useSoftphone] echo test: failed to get microphone:', err)
      setEchoTestRunning(false)
      isEchoTestCallRef.current = false
      setCallState('idle')
    }
  }, [echoTestRunning, selectedAudioInput, useStereo, enableEchoCancel, enableNoiseSupp, enableAgc])

  const stopEchoTest = useCallback(() => {
    // Stop local loopback stream
    if (echoTestStreamRef.current) {
      echoTestStreamRef.current.getTracks().forEach((track) => track.stop())
      echoTestStreamRef.current = null
    }
    // Clear audio element
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null
      remoteAudioRef.current.pause()
    }
    // Also terminate SIP sessions if any exist (fallback).
    //
    // ITEM 98 — ALL legs, not just the primary. A consultation session left
    // running after teardown is a live SIP call with no UI bound to it, holding
    // the agent's audio device.
    for (const s of sessionsRef.current.all()) {
      try {
        (s as JsSipSession).terminate()
      } catch {
        // already terminated
      }
    }
    sessionsRef.current = createSessionRegistry()
    if (sessionRef.current) {
      sessionRef.current.terminate()
    }
    isEchoTestCallRef.current = false
    setEchoTestRunning(false)
    setEchoTestPassed(true)
    setCallState('idle')
    setRemoteCLI(null)
    setCallStartTime(null)
    console.log('[useSoftphone] echo test stopped and passed')
  }, [])

  // Persist telephony options to localStorage
  useEffect(() => { try { localStorage.setItem('kc3_useStereo', String(useStereo)) } catch { /* non-fatal */ } }, [useStereo])
  useEffect(() => { try { localStorage.setItem('kc3_useStun', String(useStun)) } catch { /* non-fatal */ } }, [useStun])
  useEffect(() => { try { localStorage.setItem('kc3_enableAgc', String(enableAgc)) } catch { /* non-fatal */ } }, [enableAgc])
  useEffect(() => { try { localStorage.setItem('kc3_enableEchoCancel', String(enableEchoCancel)) } catch { /* non-fatal */ } }, [enableEchoCancel])
  useEffect(() => { try { localStorage.setItem('kc3_enableNoiseSupp', String(enableNoiseSupp)) } catch { /* non-fatal */ } }, [enableNoiseSupp])
  useEffect(() => { try { localStorage.setItem('kc3_enableHighpass', String(enableHighpass)) } catch { /* non-fatal */ } }, [enableHighpass])

  // Initialize devices on mount
  useEffect(() => {
    refreshDevices()
  }, [refreshDevices])

  return { 
    registered, 
    callState, 
    // ITEM 100 — for useWebRtcStats.
    peerConnection,
    remoteCLI, 
    isMuted,
    answer, 
    hangup, 
    hold, 
    unhold, 
    dial, 
    mute, 
    unmute,
    toggleMute,
    sendDtmf,
    resumeForTransfer,
    transfer,
    // ITEM 98 — attended transfer: consult, then complete OR cancel.
    startConsultation,
    cancelConsultation,
    completeAttendedTransfer,
    // Audio device management
    audioInputDevices,
    audioOutputDevices,
    selectedAudioInput,
    selectedAudioOutput,
    setSelectedAudioInput,
    setSelectedAudioOutput,
    refreshDevices,
    // Echo test
    echoTestPassed,
    echoTestRunning,
    runEchoTest,
    stopEchoTest,
    callStartTime,
    // Web Telephony Options
    useStereo,
    setUseStereo,
    useStun,
    setUseStun,
    enableAgc,
    setEnableAgc,
    enableEchoCancel,
    setEnableEchoCancel,
    enableNoiseSupp,
    setEnableNoiseSupp,
    enableHighpass,
    setEnableHighpass,
  }
}
